import React, { useState, useEffect } from 'react'
import { ApiService, NewsSpider, NewsSpiderExecution } from '../services/api'
import { SpiderIcon, getSpiderTypeColor, PremiumBadge } from './SpiderIcons'

interface SpiderDetailModalProps {
  spider: NewsSpider
  isOpen: boolean
  onClose: () => void
  onUpdate: (spider: NewsSpider) => void
}

const SpiderDetailModal: React.FC<SpiderDetailModalProps> = ({ 
  spider, 
  isOpen, 
  onClose, 
  onUpdate 
}) => {
  const [activeTab, setActiveTab] = useState<'config' | 'categories' | 'trigger' | 'history'>('config')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  
  // Configuration state
  const [enabled, setEnabled] = useState(spider.enabled !== false)
  const [maxArticles, setMaxArticles] = useState(spider.maxArticles)
  const [selectedCategories, setSelectedCategories] = useState<string[]>(spider.selectedCategories || [])
  const [schedule, setSchedule] = useState(spider.schedule || '')
  const [customTickers, setCustomTickers] = useState<string[]>(Array.isArray(spider.customTickers) ? spider.customTickers : [])
  const [newTicker, setNewTicker] = useState('')
  
  // Trigger state
  const [triggerPassword, setTriggerPassword] = useState('')
  const [triggerMaxArticles, setTriggerMaxArticles] = useState(spider.defaultMaxArticles)
  const [triggerCategories, setTriggerCategories] = useState<string[]>(spider.selectedCategories || [])
  const [triggerCustomTickers, setTriggerCustomTickers] = useState<string[]>(Array.isArray(spider.customTickers) ? spider.customTickers : [])
  const [triggerNewTicker, setTriggerNewTicker] = useState('')
  const [triggering, setTriggering] = useState(false)
  
  // History state
  const [executions, setExecutions] = useState<NewsSpiderExecution[]>([])
  const [executionsLoading, setExecutionsLoading] = useState(false)

  useEffect(() => {
    if (isOpen && activeTab === 'history') {
      loadExecutionHistory()
    }
  }, [isOpen, activeTab, spider.id])

  const loadExecutionHistory = async () => {
    try {
      setExecutionsLoading(true)
      const history = await ApiService.getSpiderExecutions(spider.id, 20, 0)
      setExecutions(history.executions)
    } catch (err: any) {
      console.error('Failed to load execution history:', err)
    } finally {
      setExecutionsLoading(false)
    }
  }

  const handleSaveConfig = async () => {
    try {
      setLoading(true)
      setError(null)
      setSuccess(null)

      await ApiService.updateSpiderConfig(spider.id, {
        enabled,
        maxArticles,
        selectedCategories,
        schedule,
        customTickers: spider.supportsCustomTickers ? customTickers : undefined
      })

      // Update the spider object
      const updatedSpider = {
        ...spider,
        enabled,
        maxArticles,
        selectedCategories,
        schedule,
        customTickers: spider.supportsCustomTickers ? customTickers : spider.customTickers
      }

      onUpdate(updatedSpider)
      setSuccess('Configuration saved successfully!')
      
      setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save configuration')
    } finally {
      setLoading(false)
    }
  }

  const handleTrigger = async () => {
    if (!triggerPassword.trim()) {
      setError('Password is required')
      return
    }

    try {
      setTriggering(true)
      setError(null)
      setSuccess(null)

      const result = await ApiService.triggerSpider(spider.id, triggerPassword, {
        maxArticles: triggerMaxArticles,
        selectedCategories: triggerCategories,
        customTickers: spider.supportsCustomTickers ? triggerCustomTickers : undefined
      })

      setSuccess(`Spider triggered successfully! Job ID: ${result.data.jobId}`)
      setTriggerPassword('')
      
      // Refresh execution history if we're on that tab
      if (activeTab === 'history') {
        setTimeout(() => loadExecutionHistory(), 2000)
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to trigger spider')
    } finally {
      setTriggering(false)
    }
  }

  const handleCategoryToggle = (category: string, isForTrigger = false) => {
    if (isForTrigger) {
      setTriggerCategories(prev => 
        prev.includes(category) 
          ? prev.filter(c => c !== category)
          : [...prev, category]
      )
    } else {
      setSelectedCategories(prev => 
        prev.includes(category) 
          ? prev.filter(c => c !== category)
          : [...prev, category]
      )
    }
  }

  const handleAddTicker = (isForTrigger = false) => {
    const ticker = isForTrigger ? triggerNewTicker.trim().toUpperCase() : newTicker.trim().toUpperCase()
    if (!ticker) return
    
    if (isForTrigger) {
      const currentTickers = Array.isArray(triggerCustomTickers) ? triggerCustomTickers : []
      if (!currentTickers.includes(ticker)) {
        setTriggerCustomTickers(prev => [...(Array.isArray(prev) ? prev : []), ticker])
      }
      setTriggerNewTicker('')
    } else {
      const currentTickers = Array.isArray(customTickers) ? customTickers : []
      if (!currentTickers.includes(ticker)) {
        setCustomTickers(prev => [...(Array.isArray(prev) ? prev : []), ticker])
      }
      setNewTicker('')
    }
  }

  const handleRemoveTicker = (ticker: string, isForTrigger = false) => {
    if (isForTrigger) {
      setTriggerCustomTickers(prev => Array.isArray(prev) ? prev.filter(t => t !== ticker) : [])
    } else {
      setCustomTickers(prev => Array.isArray(prev) ? prev.filter(t => t !== ticker) : [])
    }
  }

  const formatExecutionTime = (timeMs: number | null) => {
    if (!timeMs) return 'N/A'
    
    if (timeMs < 1000) return `${Math.round(timeMs)}ms`
    if (timeMs < 60000) return `${Math.round(timeMs / 1000)}s`
    return `${Math.round(timeMs / 60000)}m`
  }

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString()
  }

  if (!isOpen) return null

  const tabs = [
    { id: 'config', name: 'Configuration' },
    { id: 'categories', name: 'Categories' },
    { id: 'trigger', name: 'Manual Trigger' },
    { id: 'history', name: 'Execution History' }
  ]

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">{spider.name}</h3>
                <p className="text-sm text-gray-500">{spider.description}</p>
                <div className="mt-2 flex items-center space-x-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSpiderTypeColor(spider.type)}`}>
                    {spider.type}
                  </span>
                  {spider.premium && <PremiumBadge />}
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {enabled ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="mt-4">
              <nav className="flex space-x-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Alerts */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mx-6 mt-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
                <button 
                  onClick={() => setError(null)}
                  className="ml-auto text-red-400 hover:text-red-600"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border-l-4 border-green-400 p-4 mx-6 mt-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-green-700">{success}</p>
                </div>
                <button 
                  onClick={() => setSuccess(null)}
                  className="ml-auto text-green-400 hover:text-green-600"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="px-6 py-4 max-h-96 overflow-y-auto">
            {/* Configuration Tab */}
            {activeTab === 'config' && (
              <div className="space-y-6">
                {/* Basic Settings */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Settings</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Enabled Toggle */}
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={(e) => setEnabled(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">Enable Spider</span>
                      </label>
                      <p className="mt-1 text-xs text-gray-500">
                        When enabled, this spider can be triggered manually or by schedule
                      </p>
                    </div>

                    {/* Max Articles */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Articles per Execution
                      </label>
                      <input
                        type="number"
                        min="1"
                        max={spider.maxArticles}
                        value={maxArticles}
                        onChange={(e) => setMaxArticles(parseInt(e.target.value) || spider.defaultMaxArticles)}
                        className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Default: {spider.defaultMaxArticles}, Max: {spider.maxArticles}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Schedule */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Schedule (Optional)</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cron Expression
                    </label>
                    <input
                      type="text"
                      value={schedule}
                      onChange={(e) => setSchedule(e.target.value)}
                      placeholder="0 0 * * * (daily at midnight)"
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Leave empty for manual triggering only. Format: second minute hour day month dayOfWeek
                    </p>
                  </div>
                </div>

                {/* Spider Info */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Spider Information</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Processing Time:</span>
                        <span className="ml-2 font-medium">{spider.processingTime}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Total Categories:</span>
                        <span className="ml-2 font-medium">{spider.categories.length}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Total Executions:</span>
                        <span className="ml-2 font-medium">{spider.statistics.totalExecutions}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Success Rate:</span>
                        <span className="ml-2 font-medium">
                          {spider.statistics.totalExecutions > 0 
                            ? `${Math.round((spider.statistics.successfulExecutions / spider.statistics.totalExecutions) * 100)}%`
                            : 'N/A'
                          }
                        </span>
                      </div>
                    </div>
                    
                    <div>
                      <span className="text-gray-500 text-sm">Features:</span>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {spider.features.map((feature, index) => (
                          <span 
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-800"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Categories Tab */}
            {activeTab === 'categories' && (
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">
                    Available Categories ({spider.categories.length})
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Select the categories you want this spider to scrape. If none are selected, all categories will be used.
                  </p>
                  
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {spider.categories.map((category) => (
                      <label key={category} className="flex items-center p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(category)}
                          onChange={() => handleCategoryToggle(category)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-3 text-sm text-gray-700">{category}</span>
                      </label>
                    ))}
                  </div>

                  {selectedCategories.length > 0 && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>{selectedCategories.length}</strong> categories selected
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {selectedCategories.map((category) => (
                          <span 
                            key={category}
                            className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-800"
                          >
                            {category}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Custom Tickers for Yahoo Finance */}
                {spider.supportsCustomTickers && (
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">
                      Custom Tickers
                    </h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Add specific stock tickers to get news for. Each ticker will be added as a category with the prefix "{spider.customTickerPrefix}".
                    </p>
                    
                    {/* Add New Ticker */}
                    <div className="flex space-x-2 mb-4">
                      <input
                        type="text"
                        value={newTicker}
                        onChange={(e) => setNewTicker(e.target.value.toUpperCase())}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddTicker()}
                        placeholder="Enter ticker symbol (e.g., AAPL, TSLA)"
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        onClick={() => handleAddTicker()}
                        disabled={!newTicker.trim()}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add
                      </button>
                    </div>

                    {/* Current Tickers */}
                    {customTickers.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-700">
                          Current Tickers ({customTickers.length})
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {customTickers.map((ticker) => (
                            <span 
                              key={ticker}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800"
                            >
                              {ticker}
                              <button
                                onClick={() => handleRemoveTicker(ticker)}
                                className="ml-2 text-green-600 hover:text-green-800"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500">
                          These tickers will be sent as categories: {customTickers.map(t => `${spider.customTickerPrefix}${t}`).join(', ')}
                        </p>
                      </div>
                    )}

                    {customTickers.length === 0 && (
                      <div className="text-center py-4 border-2 border-dashed border-gray-300 rounded-lg">
                        <p className="text-sm text-gray-500">No custom tickers added yet</p>
                        <p className="text-xs text-gray-400 mt-1">Add ticker symbols to get specific stock news</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Trigger Tab */}
            {activeTab === 'trigger' && (
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Manual Trigger</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Trigger this spider manually with custom settings. This will run the spider immediately.
                  </p>

                  <div className="space-y-4">
                    {/* Password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Your Password (Required)
                      </label>
                      <input
                        type="password"
                        value={triggerPassword}
                        onChange={(e) => setTriggerPassword(e.target.value)}
                        placeholder="Enter your admin password"
                        className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    {/* Max Articles for Trigger */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Articles for this Run
                      </label>
                      <input
                        type="number"
                        min="1"
                        max={spider.maxArticles}
                        value={triggerMaxArticles}
                        onChange={(e) => setTriggerMaxArticles(parseInt(e.target.value) || spider.defaultMaxArticles)}
                        className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    {/* Categories for Trigger */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Categories for this Run
                      </label>
                      <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2">
                        {spider.categories.map((category) => (
                          <label key={category} className="flex items-center p-1 hover:bg-gray-50 rounded text-sm">
                            <input
                              type="checkbox"
                              checked={triggerCategories.includes(category)}
                              onChange={() => handleCategoryToggle(category, true)}
                              className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-gray-700">{category}</span>
                          </label>
                        ))}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        {triggerCategories.length} categories selected
                      </p>
                    </div>

                    {/* Custom Tickers for Trigger */}
                    {spider.supportsCustomTickers && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Custom Tickers for this Run
                        </label>
                        
                        {/* Add New Ticker for Trigger */}
                        <div className="flex space-x-2 mb-3">
                          <input
                            type="text"
                            value={triggerNewTicker}
                            onChange={(e) => setTriggerNewTicker(e.target.value.toUpperCase())}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddTicker(true)}
                            placeholder="Add ticker for this run (e.g., AAPL)"
                            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          />
                          <button
                            onClick={() => handleAddTicker(true)}
                            disabled={!triggerNewTicker.trim()}
                            className="bg-blue-600 text-white px-3 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Add
                          </button>
                        </div>

                        {/* Current Trigger Tickers */}
                        {triggerCustomTickers.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-2">
                            {triggerCustomTickers.map((ticker) => (
                              <span 
                                key={ticker}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800"
                              >
                                {ticker}
                                <button
                                  onClick={() => handleRemoveTicker(ticker, true)}
                                  className="ml-1 text-green-600 hover:text-green-800"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        
                        <p className="text-xs text-gray-500">
                          {triggerCustomTickers.length} custom tickers for this run
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-medium text-gray-900">Execution History</h4>
                  <div className="flex space-x-2">
                    <button
                      onClick={async () => {
                        try {
                          setLoading(true)
                          const result = await ApiService.updateSpiderArticleCount(spider.id)
                          setSuccess(`Article count updated: ${result.data.oldCount} → ${result.data.newCount} articles`)
                          setTimeout(() => loadExecutionHistory(), 1000) // Refresh history after update
                        } catch (err: any) {
                          if (err.response?.status === 404) {
                            setError('Article count update feature not yet deployed to production. Please wait for next deployment.')
                          } else {
                            setError(err.response?.data?.message || 'Failed to update article count')
                          }
                        } finally {
                          setLoading(false)
                        }
                      }}
                      disabled={loading || executionsLoading}
                      className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      {loading ? 'Updating...' : 'Update Count'}
                    </button>
                    <button
                      onClick={loadExecutionHistory}
                      disabled={executionsLoading}
                      className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                    >
                      {executionsLoading ? 'Loading...' : 'Refresh'}
                    </button>
                  </div>
                </div>

                {/* Latest Execution Info */}
                {executions.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h5 className="text-sm font-medium text-blue-900 mb-2">Latest Execution</h5>
                    <div className="text-sm text-blue-800 space-y-1">
                      <p>Status: <span className="font-medium">{executions[0].status}</span></p>
                      <p>Articles: <span className="font-medium">{executions[0].articles_scraped || 0}</span></p>
                      <p>Duration: <span className="font-medium">{formatExecutionTime(executions[0].execution_time_ms)}</span></p>
                      <p className="text-xs text-blue-600 mt-2">
                        💡 Article counts are updated automatically 5 minutes after execution. Use "Update Count" for immediate refresh.
                      </p>
                    </div>
                  </div>
                )}

                {executionsLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-600 mt-2">Loading execution history...</p>
                  </div>
                ) : executions.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No execution history found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {executions.map((execution) => (
                      <div key={execution.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              execution.status === 'success' ? 'bg-green-100 text-green-800' :
                              execution.status === 'failed' ? 'bg-red-100 text-red-800' :
                              execution.status === 'running' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {execution.status}
                            </span>
                            <span className="text-xs text-gray-500">
                              {execution.trigger_type}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {formatDateTime(execution.created_at)}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                          <div>
                            <span className="text-gray-500">Articles:</span>
                            <span className="ml-1 font-medium">{execution.articles_scraped}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Duration:</span>
                            <span className="ml-1 font-medium">{formatExecutionTime(execution.execution_time_ms)}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Triggered by:</span>
                            <span className="ml-1 font-medium">{execution.triggered_by_email}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Job ID:</span>
                            <span className="ml-1 font-medium font-mono text-xs">{execution.job_id}</span>
                          </div>
                        </div>

                        {execution.error_message && (
                          <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-700">
                            <strong>Error:</strong> {execution.error_message}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 flex justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Close
            </button>
            
            <div className="flex space-x-3">
              {activeTab === 'config' && (
                <button
                  onClick={handleSaveConfig}
                  disabled={loading}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Configuration'}
                </button>
              )}
              
              {activeTab === 'trigger' && (
                <button
                  onClick={handleTrigger}
                  disabled={triggering || !triggerPassword.trim()}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                >
                  {triggering ? 'Triggering...' : 'Trigger Spider'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SpiderDetailModal 